import React, { useState } from 'react';

// File Upload Component with Industry Best Practices
const ImageUpload = ({ currentImage, onImageChange, label = "Profile Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [imageError, setImageError] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Configuration constants matching backend
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const MIN_DIMENSION = 200; // Minimum 200px
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const API = `${BASE_URL}/api`;

  React.useEffect(() => {
    if (currentImage && currentImage.trim() !== '') {
      setImageError(false);
      setValidationError('');
      // If currentImage starts with /uploads/, it's a relative path, prepend base URL with /api
      if (currentImage.startsWith('/uploads/')) {
        const fullUrl = `${BASE_URL}/api${currentImage}`;
        console.log('Setting preview URL:', fullUrl);
        setPreviewUrl(fullUrl);
      } else if (currentImage.startsWith('/api/uploads/')) {
        // Already has the correct API path
        const fullUrl = `${BASE_URL}${currentImage}`;
        console.log('Setting preview URL (API path):', fullUrl);
        setPreviewUrl(fullUrl);
      } else if (currentImage.startsWith('http')) {
        // It's already a full URL
        setPreviewUrl(currentImage);
      } else if (currentImage) {
        // Assume it's a relative path and prepend base URL with API path
        const fullUrl = `${BASE_URL}/api/uploads/${currentImage}`;
        console.log('Setting preview URL (relative):', fullUrl);
        setPreviewUrl(fullUrl);
      }
    } else {
      setPreviewUrl('');
      setImageError(false);
    }
  }, [currentImage, BASE_URL]);

  const handleImageError = () => {
    console.error('Image failed to load:', previewUrl);
    setImageError(true);
    // Only clear invalid URLs, not all image failures
    if (currentImage && !currentImage.startsWith('http') && !currentImage.startsWith('/uploads/')) {
      console.log('Clearing invalid image URL:', currentImage);
      onImageChange('');
    }
  };

  const handleImageLoad = () => {
    console.log('Image loaded successfully:', previewUrl);
    setImageError(false);
  };

  // Client-side validation before upload
  const validateImageFile = (file) => {
    return new Promise((resolve, reject) => {
      // Check file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        reject('Invalid file type. Please use JPEG, PNG, GIF, or WebP.');
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        reject(`File too large. Maximum size is 2MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
        return;
      }

      // Check image dimensions
      const img = new window.Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
          reject(`Image too small. Minimum dimensions: ${MIN_DIMENSION}x${MIN_DIMENSION}px. Your image: ${img.width}x${img.height}px.`);
        } else {
          resolve({ width: img.width, height: img.height });
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject('Could not read image file. Please try another image.');
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setValidationError('');

    try {
      // Client-side validation
      await validateImageFile(file);

      setUploading(true);

      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Upload file
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Upload result:', result);
        
        // Set preview URL with full path
        const fullImageUrl = `${BASE_URL}/api${result.file_url}`;
        setPreviewUrl(fullImageUrl);
        setImageError(false);
        
        // Store the relative path in the form
        onImageChange(result.file_url);
      } else {
        const error = await response.json();
        console.error('Upload error:', error);
        setValidationError(error.detail || 'Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Validation/Upload error:', error);
      setValidationError(typeof error === 'string' ? error : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleRemoveImage = async () => {
    if (!currentImage) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/upload/image?file_url=${encodeURIComponent(currentImage)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error('Delete failed:', await response.text());
      }
    } catch (error) {
      console.error('Delete error:', error);
    }

    setPreviewUrl('');
    setImageError(false);
    setValidationError('');
    onImageChange('');
  };

  return (
    <div className="image-upload-container">
      <label className="image-upload-label">{label}</label>
      <div className="image-upload-area">
        {previewUrl && !imageError ? (
          <div className="image-preview">
            <img 
              src={previewUrl} 
              alt="Preview" 
              className="preview-image"
              crossOrigin="anonymous"
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
            <div className="image-overlay">
              <button
                type="button"
                onClick={handleRemoveImage}
                className="remove-image-btn"
                title="Remove image"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <label className="upload-placeholder">
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              disabled={uploading}
              className="file-input"
            />
            <div className="upload-content">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-icon">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <span>
                {uploading ? 'Uploading...' : 
                 imageError ? 'Upload New Image' : 
                 'Add Image'}
              </span>
            </div>
          </label>
        )}
      </div>
      {validationError && (
        <small className="image-error validation-error">{validationError}</small>
      )}
      {imageError && previewUrl && currentImage && !validationError && (
        <small className="image-error">Unable to load image. Please try uploading a new one.</small>
      )}
      <small className="image-requirements">Min: 200×200px • Max: 2MB • JPEG, PNG, GIF, WebP</small>
    </div>
  );
};

// Tag Input Component for Instruments
const TagInput = ({ tags = [], onChange, placeholder = "Add tags...", label }) => {
  const [inputValue, setInputValue] = useState('');
  
  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // If user types a comma, add the tag
    if (value.includes(',')) {
      const newTag = value.replace(',', '').trim().toUpperCase();
      if (newTag && !tags.includes(newTag)) {
        const newTags = [...tags, newTag];
        onChange(newTags);
      }
      setInputValue('');
    } else {
      setInputValue(value);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const newTag = inputValue.trim().toUpperCase();
      if (newTag && !tags.includes(newTag)) {
        const newTags = [...tags, newTag];
        onChange(newTags);
      }
      setInputValue('');
    }
  };

  const removeTag = (tagToRemove) => {
    const newTags = tags.filter(tag => tag !== tagToRemove);
    onChange(newTags);
  };

  return (
    <div className="form-field">
      {label && <label>{label}</label>}
      <div className="tag-input-container">
        <div className="tags-display">
          {tags.map((tag, index) => (
            <span key={index} className="instrument-tag">
              {tag}
              <button 
                type="button" 
                onClick={() => removeTag(tag)}
                className="tag-remove-btn"
                title="Remove tag"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="tag-input"
        />
      </div>
      <small className="field-help">Type instrument names and press Enter or comma to add tags</small>
    </div>
  );
};

// Personal Details Tab Component
export const PersonalDetailsTab = ({ profile, onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    bio: '',
    profile_image: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const API = `${BASE_URL}/api`;

  // Update form data when profile changes - use JSON comparison for deep equality
  React.useEffect(() => {
    if (profile && Object.keys(profile).length > 0) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        bio: profile.bio || '',
        profile_image: profile.profile_image || ''
      });
      setHasUnsavedChanges(false);
    }
  }, [profile?.id, profile?.profile_image, profile?.first_name, profile?.last_name, profile?.email, profile?.phone, profile?.bio]);

  // Auto-save when image changes
  const handleImageChange = async (imageUrl) => {
    const updatedFormData = {...formData, profile_image: imageUrl};
    setFormData(updatedFormData);
    setHasUnsavedChanges(true);
    
    // Auto-save the profile when image changes
    if (imageUrl) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BASE_URL}/api/profile`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedFormData),
        });

        if (response.ok) {
          setMessage('Profile image saved successfully!');
          setHasUnsavedChanges(false);
          onProfileUpdate();
        } else {
          setMessage('Error saving profile image');
        }
      } catch (error) {
        setMessage('Error saving profile image');
        console.error('Error:', error);
      }
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData({...formData, [field]: value});
    setHasUnsavedChanges(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setMessage('Profile updated successfully!');
        setHasUnsavedChanges(false);
        onProfileUpdate();
      } else {
        setMessage('Error updating profile');
      }
    } catch (error) {
      setMessage('Error updating profile');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-tab-content">
      <div className="tab-header">
        <div className="tab-header-with-image">
          {formData.profile_image ? (
            <img 
              src={
                formData.profile_image.startsWith('/uploads/') 
                  ? `${BASE_URL}/api${formData.profile_image}`
                  : formData.profile_image.startsWith('/api/uploads/')
                  ? `${BASE_URL}${formData.profile_image}`
                  : formData.profile_image.startsWith('http')
                  ? formData.profile_image
                  : `${BASE_URL}/api/uploads/${formData.profile_image}`
              } 
              alt="Profile" 
              className="header-profile-image"
              crossOrigin="anonymous"
              onError={(e) => {
                console.error('Header profile image failed to load:', e.target.src);
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="header-profile-placeholder">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
          )}
          <h3>Personal Details</h3>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-row">
          <div className="form-field">
            <label>First Name</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => handleFieldChange('first_name', e.target.value)}
              placeholder="Enter your first name"
            />
          </div>
          <div className="form-field">
            <label>Last Name</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => handleFieldChange('last_name', e.target.value)}
              placeholder="Enter your last name"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-field">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              placeholder="Enter your email"
            />
          </div>
          <div className="form-field">
            <label>Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              placeholder="Enter your phone number"
            />
          </div>
        </div>

        <div className="form-field">
          <label>Bio</label>
          <textarea
            value={formData.bio}
            onChange={(e) => handleFieldChange('bio', e.target.value)}
            placeholder="Tell us about yourself"
            rows={4}
          />
        </div>

        <ImageUpload
          currentImage={formData.profile_image}
          onImageChange={handleImageChange}
          label="Profile Image"
        />

        <button type="submit" disabled={loading} className={`btn ${hasUnsavedChanges ? 'btn-warning' : 'btn-primary'}`}>
          {loading ? 'Updating...' : hasUnsavedChanges ? 'Save Changes' : 'Update Profile'}
        </button>

        {hasUnsavedChanges && (
          <p className="unsaved-changes-notice">
            You have unsaved changes. Click "Save Changes" to save them.
          </p>
        )}

        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
      </form>
    </div>
  );
};

// Artists Tab Component
export const ArtistsTab = ({ artists, onArtistsUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    profile_image: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [artistToDelete, setArtistToDelete] = useState(null);
  
  // Duplicate checking states
  const [duplicateCheckMessage, setDuplicateCheckMessage] = useState('');
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const API = `${BASE_URL}/api`;

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', profile_image: '' });
    setEditingArtist(null);
    setShowForm(false);
    setMessage('');
    setShowDeleteModal(false);
    setArtistToDelete(null);
    setDuplicateCheckMessage('');
    setIsCheckingDuplicate(false);
  };

  const handleEdit = (artist) => {
    setFormData({
      name: artist.name || '',
      email: artist.email || '',
      phone: artist.phone || '',
      profile_image: artist.profile_image || ''
    });
    setEditingArtist(artist);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      
      // Check for duplicates before creating/updating
      if (!editingArtist || 
          editingArtist.name !== formData.name.trim() || 
          editingArtist.email !== formData.email.trim()) {
        
        const params = new URLSearchParams();
        if (formData.name.trim()) params.append('name', formData.name.trim());
        if (formData.email.trim()) params.append('email', formData.email.trim());
        // Add exclude_id when editing to prevent self-comparison
        if (editingArtist) params.append('exclude_id', editingArtist.id);
        
        if (params.toString()) {
          const duplicateCheckResponse = await fetch(
            `${API}/artists/check-duplicate?${params.toString()}`, 
            {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            }
          );
          
          if (duplicateCheckResponse.ok) {
            const duplicateData = await duplicateCheckResponse.json();
            
            if (duplicateData.is_duplicate) {
              setMessage(`${duplicateData.message}`);
              setLoading(false);
              return;
            }
          }
        }
      }
      
      const url = editingArtist 
        ? `${API}/artists/${editingArtist.id}`
        : `${API}/artists`;
      
      const method = editingArtist ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setMessage(`Artist ${editingArtist ? 'updated' : 'created'} successfully!`);
        onArtistsUpdate();
        
        // For edits, show success message for 2 seconds before closing form
        if (editingArtist) {
          setTimeout(() => {
            resetForm();
          }, 2000);
        } else {
          // For new artists, close form immediately
          resetForm();
        }
      } else {
        const errorData = await response.json();
        setMessage(`${errorData.detail || 'Error saving artist'}`);
      }
    } catch (error) {
      setMessage('Error saving artist');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (artist) => {
    setArtistToDelete(artist);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!artistToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/artists/${artistToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage('Artist deleted successfully!');
        onArtistsUpdate();
        setShowDeleteModal(false);
        setArtistToDelete(null);
      } else {
        setMessage('Error deleting artist');
      }
    } catch (error) {
      setMessage('Error deleting artist');
      console.error('Error:', error);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setArtistToDelete(null);
  };

  const checkArtistDuplicate = async (name, email) => {
    if (!name && !email) {
      setDuplicateCheckMessage('');
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (name && name.length >= 2) params.append('name', name);
      if (email) params.append('email', email);

      if (params.toString()) {
        const response = await fetch(
          `${API}/artists/check-duplicate?${params.toString()}`, 
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.is_duplicate) {
            setDuplicateCheckMessage(`Warning: ${data.message}`);
          } else {
            setDuplicateCheckMessage('Available: Artist details are available');
          }
        }
      }
    } catch (error) {
      console.error('Error checking artist duplicate:', error);
      setDuplicateCheckMessage('');
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  // Debounced duplicate checking for artist data
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.name || formData.email) {
        checkArtistDuplicate(formData.name, formData.email);
      } else {
        setDuplicateCheckMessage('');
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.name, formData.email]);

  return (
    <div className="settings-tab-content">
      <div className="tab-header">
        <h3>Artists</h3>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          Add Artist
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {showForm && (
        <div className="form-modal-overlay">
          <div className="form-modal">
            <div className="modal-header">
              <h4>{editingArtist ? 'Edit Artist' : 'Add New Artist'}</h4>
              <button onClick={resetForm} className="close-btn">&times;</button>
            </div>
            
            <form onSubmit={handleSubmit} className="settings-form">
              <div className="form-field">
                <label>Artist Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter artist name"
                  required
                />
                {isCheckingDuplicate && (
                  <small className="field-help checking">
                    🔍 Checking availability...
                  </small>
                )}
                {duplicateCheckMessage && !isCheckingDuplicate && (
                  <small className={`field-help ${duplicateCheckMessage.startsWith('Warning') ? 'warning' : duplicateCheckMessage.startsWith('Available') ? 'success' : 'info'}`}>
                    {duplicateCheckMessage.replace(/^(Warning|Available|Info): /, '')}
                  </small>
                )}
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="Enter email"
                  />
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <ImageUpload
                currentImage={formData.profile_image}
                onImageChange={(imageUrl) => setFormData({...formData, profile_image: imageUrl})}
                label="Artist Profile Image"
              />

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn btn-primary">
                  {loading ? 'Saving...' : (editingArtist ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {artists.length === 0 ? (
        <div className="empty-state">
          <p>No artists found. Click "Add Artist" to create your first artist profile.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="gigs-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {artists.map(artist => (
                <tr key={artist.id}>
                  <td>
                    {artist.profile_image ? (
                      <img 
                        src={
                          artist.profile_image.startsWith('/uploads/') 
                            ? `${BASE_URL}/api${artist.profile_image}`
                            : artist.profile_image.startsWith('/api/uploads/')
                            ? `${BASE_URL}${artist.profile_image}`
                            : artist.profile_image.startsWith('http')
                            ? artist.profile_image
                            : `${BASE_URL}/api/uploads/${artist.profile_image}`
                        } 
                        alt={artist.name} 
                        className="table-profile-image"
                        crossOrigin="anonymous"
                        onError={(e) => {
                          console.error('Artist image failed to load:', e.target.src);
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeD0iMTAiIHk9IjEwIj4KPHBhdGggZD0iTTIwIDIxVjE5QzIwIDE3LjkgMTkuMSAxNyAxOCAxN0g2QzQuOSAxNyA0IDE3LjkgNCAxOVYyMSIgc3Ryb2tlPSIjOWNhM2FmIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8Y2lyY2xlIGN4PSIxMiIgY3k9IjkiIHI9IjQiIHN0cm9rZT0iIzljYTNhZiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cjwvc3ZnPgo=';
                        }}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div className="placeholder-avatar">
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                          <circle cx="20" cy="20" r="20" fill="#f3f4f6"/>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" x="10" y="10">
                            <path d="M20 21V19C20 17.9 19.1 17 18 17H6C4.9 17 4 17.9 4 19V21" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="9" r="4" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </svg>
                      </div>
                    )}
                  </td>
                  <td><strong>{artist.name}</strong></td>
                  <td>{artist.email || '—'}</td>
                  <td>{artist.phone || '—'}</td>
                  <td>
                    <div className="actions-inline">
                      <button
                        className="btn-icon-action btn-edit"
                        onClick={() => handleEdit(artist)}
                        title="Edit artist"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button
                        className="btn-icon-action btn-delete"
                        onClick={() => handleDeleteClick(artist)}
                        title="Delete artist"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3,6 5,6 21,6"/>
                          <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal">
          <div className="modal-content small-modal">
            <div className="modal-header">
              <h4>Delete Artist?</h4>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{artistToDelete?.name}</strong>?</p>
              <p><small>This action cannot be undone.</small></p>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                onClick={cancelDelete} 
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={confirmDelete} 
                className="btn btn-danger"
              >
                Delete Artist
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Venues Tab Component
export const VenuesTab = ({ venues, onVenuesUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    is_global: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Popular venue search states
  const [venueSearchMode, setVenueSearchMode] = useState('custom'); // 'custom' or 'search'
  const [searchCity, setSearchCity] = useState('');
  const [popularVenues, setPopularVenues] = useState([]);
  const [searchingVenues, setSearchingVenues] = useState(false);
  
  // Delete confirmation modal states for venues
  const [showVenueDeleteModal, setShowVenueDeleteModal] = useState(false);
  const [venueToDelete, setVenueToDelete] = useState(null);
  const [duplicateCheckMessage, setDuplicateCheckMessage] = useState('');
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  
  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [friendToDelete, setFriendToDelete] = useState(null);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const API = `${BASE_URL}/api`;

  const resetForm = () => {
    setFormData({ name: '', address: '', city: '', is_global: false });
    setEditingVenue(null);
    setShowForm(false);
    setMessage('');
    setVenueSearchMode('custom');
    setSearchCity('');
    setPopularVenues([]);
    setShowVenueDeleteModal(false);
    setVenueToDelete(null);
    setDuplicateCheckMessage('');
    setIsCheckingDuplicate(false);
  };

  const handleEdit = (venue) => {
    setFormData({
      name: venue.name || '',
      address: venue.address || '',
      city: venue.city || '',
      is_global: venue.is_global || false
    });
    setEditingVenue(venue);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      
      // Check for duplicates before creating (for both new venues and edits with name changes)
      if (formData.name.trim() && (!editingVenue || editingVenue.name !== formData.name.trim())) {
        let duplicateCheckUrl = `${API}/venues/check-duplicate?name=${encodeURIComponent(formData.name.trim())}&is_global=${formData.is_global}`;
        // Add exclude_id when editing to prevent self-comparison
        if (editingVenue) {
          duplicateCheckUrl += `&exclude_id=${editingVenue.id}`;
        }
        
        const duplicateCheckResponse = await fetch(duplicateCheckUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (duplicateCheckResponse.ok) {
          const duplicateData = await duplicateCheckResponse.json();
          
          if (duplicateData.is_duplicate) {
            setMessage(`${duplicateData.message}`);
            setLoading(false);
            return;
          }
          
          if (duplicateData.has_global_match) {
            setMessage(`${duplicateData.message}`);
            // Allow creation but show the info message
          }
        }
      }
      
      const url = editingVenue 
        ? `${API}/venues/${editingVenue.id}`
        : `${API}/venues`;
      
      const method = editingVenue ? 'PUT' : 'POST';

      // Prepare submit data
      const submitData = {
        ...formData
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        setMessage(`Venue ${editingVenue ? 'updated' : 'created'} successfully!`);
        onVenuesUpdate();
        
        // For edits, show success message for 2 seconds before closing form
        if (editingVenue) {
          setTimeout(() => {
            resetForm();
          }, 2000);
        } else {
          // For new venues, close form immediately
          resetForm();
        }
      } else {
        setMessage('Error saving venue');
      }
    } catch (error) {
      setMessage('Error saving venue');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (venue) => {
    setVenueToDelete(venue);
    setShowVenueDeleteModal(true);
  };

  const confirmDeleteVenue = async () => {
    if (!venueToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/venues/${venueToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage('Venue deleted successfully!');
        onVenuesUpdate();
        setShowVenueDeleteModal(false);
        setVenueToDelete(null);
      } else {
        setMessage('Error deleting venue');
      }
    } catch (error) {
      setMessage('Error deleting venue');
      console.error('Error:', error);
    }
  };

  const cancelDeleteVenue = () => {
    setShowVenueDeleteModal(false);
    setVenueToDelete(null);
  };

  const searchPopularVenues = async (city) => {
    if (!city || city.length < 2) {
      setPopularVenues([]);
      return;
    }

    setSearchingVenues(true);
    try {
      // Check for existing venues in user's list (exact name match)
      const existingVenue = venues.find(venue => 
        venue.name.toLowerCase().trim() === city.toLowerCase().trim()
      );
      
      if (existingVenue) {
        // Show "venue already exists" message instead of search results
        setPopularVenues([{ 
          isExistingVenue: true, 
          existingVenueName: existingVenue.name,
          existingVenueType: existingVenue.is_global ? 'global' : 'personal'
        }]);
        setSearchingVenues(false);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/venues/popular/search?city=${encodeURIComponent(city)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const venues = await response.json();
        setPopularVenues(venues);
      } else {
        // If API doesn't exist yet, provide some example venues based on city
        const exampleVenues = getExampleVenuesForCity(city);
        setPopularVenues(exampleVenues);
      }
    } catch (error) {
      console.error('Error searching popular venues:', error);
      // Fallback to example venues
      const exampleVenues = getExampleVenuesForCity(city);
      setPopularVenues(exampleVenues);
    } finally {
      setSearchingVenues(false);
    }
  };

  const getExampleVenuesForCity = (city) => {
    const cityLower = city.toLowerCase();
    
    // Only return venues for cities we have real data for
    // Support partial matches (e.g., "mel" matches "melbourne")
    if (cityLower.includes('melbourne') || cityLower.includes('melb') || cityLower.includes('mel')) {
      return [
        { name: 'The Palais Theatre', address: '14 Lower Esplanade, St Kilda VIC', city: 'Melbourne' },
        { name: 'Corner Hotel', address: '57 Swan St, Richmond VIC', city: 'Melbourne' },
        { name: 'The Forum Melbourne', address: '154 Flinders St, Melbourne VIC', city: 'Melbourne' },
        { name: 'Hamer Hall', address: '100 St Kilda Rd, Southbank VIC', city: 'Melbourne' }
      ];
    } else if (cityLower.includes('sydney') || cityLower.includes('syd')) {
      return [
        { name: 'Sydney Opera House', address: 'Bennelong Point, Sydney NSW', city: 'Sydney' },
        { name: 'Enmore Theatre', address: '118-132 Enmore Rd, Enmore NSW', city: 'Sydney' },
        { name: 'Metro Theatre', address: '624 George St, Sydney NSW', city: 'Sydney' },
        { name: 'The Basement', address: '29 Reiby Pl, Sydney NSW', city: 'Sydney' }
      ];
    } else if (cityLower.includes('brisbane') || cityLower.includes('bris') || cityLower.includes('bne')) {
      return [
        { name: 'Queensland Performing Arts Centre', address: 'Cultural Centre, South Brisbane QLD', city: 'Brisbane' },
        { name: 'The Triffid', address: '7-9 Stratton St, Newstead QLD', city: 'Brisbane' },
        { name: 'Fortitude Music Hall', address: '2 Warner St, Fortitude Valley QLD', city: 'Brisbane' }
      ];
    } else if (cityLower.includes('perth') || cityLower.includes('per')) {
      return [
        { name: 'Perth Concert Hall', address: '5 St Georges Terrace, Perth WA', city: 'Perth' },
        { name: 'Fremantle Arts Centre', address: '1 Finnerty St, Fremantle WA', city: 'Perth' },
        { name: 'Astor Theatre', address: '659 Beaufort St, Mount Lawley WA', city: 'Perth' }
      ];
    } else if (cityLower.includes('adelaide') || cityLower.includes('adel') || cityLower.includes('ade')) {
      return [
        { name: 'Adelaide Festival Centre', address: 'King William Rd, Adelaide SA', city: 'Adelaide' },
        { name: 'Thebarton Theatre', address: '112 Torrington St, Thebarton SA', city: 'Adelaide' },
        { name: 'The Gov', address: '59 Port Rd, Hindmarsh SA', city: 'Adelaide' }
      ];
    }
    
    // Return empty array for cities without real venue data
    // This will trigger the "Venue not added yet" message
    return [];
  };

  const selectPopularVenue = (venue) => {
    setFormData({
      name: venue.name,
      address: venue.address,
      city: venue.city,
      is_global: false
    });
    setVenueSearchMode('custom');
    setPopularVenues([]);
    setSearchCity('');
    // Check for duplicates when a venue is selected
    checkVenueDuplicate(venue.name, false);
  };

  const checkVenueDuplicate = async (venueName, isGlobal) => {
    if (!venueName || venueName.length < 2) {
      setDuplicateCheckMessage('');
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      const token = localStorage.getItem('token');
      let duplicateCheckUrl = `${API}/venues/check-duplicate?name=${encodeURIComponent(venueName)}&is_global=${isGlobal}`;
      // Add exclude_id when editing to prevent self-comparison
      if (editingVenue) {
        duplicateCheckUrl += `&exclude_id=${editingVenue.id}`;
      }
      
      const response = await fetch(duplicateCheckUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.is_duplicate) {
          setDuplicateCheckMessage(`Warning: ${data.message}`);
        } else if (data.has_global_match) {
          setDuplicateCheckMessage(`Info: ${data.message}`);
        } else {
          setDuplicateCheckMessage('Available: Venue name is available');
        }
      }
    } catch (error) {
      console.error('Error checking venue duplicate:', error);
      setDuplicateCheckMessage('');
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  // Debounced duplicate checking for venue name
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.name && formData.name.length >= 2) {
        checkVenueDuplicate(formData.name, formData.is_global);
      } else {
        setDuplicateCheckMessage('');
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.name, formData.is_global]);

  return (
    <div className="settings-tab-content">
      <div className="tab-header">
        <h3>Venues</h3>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          Add Venue
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {showForm && (
        <div className="form-modal-overlay">
          <div className="form-modal">
            <div className="modal-header">
              <h4>{editingVenue ? 'Edit Venue' : 'Add New Venue'}</h4>
              <button onClick={resetForm} className="close-btn">&times;</button>
            </div>

            {!editingVenue && (
              <div className="venue-creation-mode">
                <div className="mode-selector">
                  <button 
                    type="button"
                    className={`mode-btn ${venueSearchMode === 'custom' ? 'active' : ''}`}
                    onClick={() => {setVenueSearchMode('custom'); setPopularVenues([]);}}
                  >
                    Create Custom Venue
                  </button>
                  <button 
                    type="button"
                    className={`mode-btn secondary ${venueSearchMode === 'search' ? 'active' : ''}`}
                    onClick={() => setVenueSearchMode('search')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="search-icon-btn">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.35-4.35"/>
                    </svg>
                    Search Venues
                  </button>
                </div>
              </div>
            )}

            {!editingVenue && venueSearchMode === 'search' && (
              <div className="popular-venue-search">
                <div className="form-field">
                  <label>Search venues in your city</label>
                  <input
                    type="text"
                    value={searchCity}
                    onChange={(e) => {
                      setSearchCity(e.target.value);
                      searchPopularVenues(e.target.value);
                    }}
                    placeholder="Enter city name (e.g., Melbourne, Sydney, Brisbane...)"
                    className="search-input"
                  />
                </div>
                
                {searchingVenues && (
                  <div className="searching-venues">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="search-loading-icon">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.35-4.35"/>
                    </svg>
                    <span>Searching popular venues...</span>
                  </div>
                )}

                {popularVenues.length > 0 && (
                  <div className="popular-venues-results">
                    {popularVenues[0].isExistingVenue ? (
                      <div className="existing-venue-message">
                        <h5>Venue Already Exists</h5>
                        <div className="existing-venue-info">
                          <div className="venue-name">
                            <strong>{popularVenues[0].existingVenueName}</strong>
                          </div>
                          <div className="venue-type">
                            {popularVenues[0].existingVenueType === 'global' ? (
                              <span className="venue-type global">Global Venue</span>
                            ) : (
                              <span className="venue-type personal">📍 Your Personal Venue</span>
                            )}
                          </div>
                          <small className="venue-help">
                            This venue is already in your list. You can use it when creating gigs.
                          </small>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h5>Popular venues in {searchCity}:</h5>
                        <div className="venue-options">
                          {popularVenues.map((venue, index) => (
                            <div 
                              key={index} 
                              className="venue-option"
                              onClick={() => selectPopularVenue(venue)}
                            >
                              <div className="venue-name">{venue.name}</div>
                              <div className="venue-address">{venue.address}</div>
                              <small className="venue-city">{venue.city}</small>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {searchCity.length >= 2 && popularVenues.length === 0 && !searchingVenues && (
                  <div className="no-venues-found">
                    <p><strong>Venue not added yet</strong></p>
                    <p><small>Switch to "Create Custom Venue" to add this venue</small></p>
                  </div>
                )}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="settings-form">
              {(editingVenue || venueSearchMode === 'custom') && (
                <>
                  <div className="form-field">
                    <label>Venue Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Enter venue name"
                      required
                    />
                    {isCheckingDuplicate && (
                      <small className="field-help checking">
                        🔍 Checking availability...
                      </small>
                    )}
                    {duplicateCheckMessage && !isCheckingDuplicate && (
                      <small className={`field-help ${duplicateCheckMessage.startsWith('Warning') ? 'warning' : duplicateCheckMessage.startsWith('Available') ? 'success' : 'info'}`}>
                        {duplicateCheckMessage.replace(/^(Warning|Available|Info): /, '')}
                      </small>
                    )}
                  </div>

                  <div className="form-field">
                    <label>Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder="Enter full address"
                    />
                  </div>

                  <div className="form-field">
                    <label>City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="Enter city"
                    />
                  </div>

                  <div className="form-field">
                    <label>Type</label>
                    <select
                      value={formData.is_global ? 'global' : 'personal'}
                      onChange={(e) => setFormData({...formData, is_global: e.target.value === 'global'})}
                      className="venue-type-select"
                    >
                      <option value="personal">Personal</option>
                      <option value="global">Global</option>
                    </select>
                    {formData.is_global && (
                      <small className="field-help">
                        Global venues will be visible to all users of the app
                      </small>
                    )}
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={resetForm} className="btn btn-secondary">
                      Cancel
                    </button>
                    <button type="submit" disabled={loading} className="btn btn-primary">
                      {loading ? 'Saving...' : (editingVenue ? 'Update' : 'Create')}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}

      {venues.length === 0 ? (
        <div className="empty-state">
          <p>No venues found. Click "Add Venue" to create your first venue profile.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="gigs-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>City</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {venues.map(venue => (
                <tr key={venue.id}>
                  <td>
                    {venue.is_global && <span className="global-indicator"></span>}
                    <strong>{venue.name}</strong>
                  </td>
                  <td>{venue.address || '—'}</td>
                  <td>{venue.city || '—'}</td>
                  <td>
                    {venue.is_global ? (
                      <span className="venue-type global">Global</span>
                    ) : (
                      <span className="venue-type personal">Personal</span>
                    )}
                  </td>
                  <td>
                    <div className="actions-inline">
                      <button
                        className="btn-icon-action btn-edit"
                        onClick={() => handleEdit(venue)}
                        title="Edit venue"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button
                        className="btn-icon-action btn-delete"
                        onClick={() => handleDeleteClick(venue)}
                        title="Delete venue"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3,6 5,6 21,6"/>
                          <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showVenueDeleteModal && (
        <div className="modal">
          <div className="modal-content small-modal">
            <div className="modal-header">
              <h4>Delete Venue?</h4>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{venueToDelete?.name}</strong>?</p>
              <p><small>This action cannot be undone.</small></p>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                onClick={cancelDeleteVenue} 
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={confirmDeleteVenue} 
                className="btn btn-danger"
              >
                Delete Venue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Friends Tab Component
export const FriendsTab = ({ friends, onFriendsUpdate, onInviteUser }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingFriend, setEditingFriend] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    profile_image: '',
    app_user_id: '',
    instruments: []
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [friendType, setFriendType] = useState('manual'); // 'manual' or 'app_user'
  
  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [friendToDelete, setFriendToDelete] = useState(null);
  
  // Duplicate checking states
  const [duplicateCheckMessage, setDuplicateCheckMessage] = useState('');
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState(null);
  const [isInviting, setIsInviting] = useState(false);

  // Phone number formatting function
  const formatPhoneNumber = (phone) => {
    if (!phone) return '—';
    
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // If it's not 10 digits, return as-is with fallback formatting
    if (cleaned.length !== 10) {
      return phone;
    }
    
    // Format as 0000 000 000
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 10)}`;
  };

  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const API = `${BASE_URL}/api`;

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', profile_image: '', app_user_id: '', instruments: [] });
    setEditingFriend(null);
    setShowForm(false);
    setMessage('');
    setSearchQuery('');
    setSearchResults([]);
    setFriendType('manual');
    setShowDeleteModal(false);
    setFriendToDelete(null);
    setDuplicateCheckMessage('');
    setIsCheckingDuplicate(false);
  };

  const searchAppUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/friends/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const handleEdit = (friend) => {
    setFormData({
      name: friend.name || '',
      email: friend.email || '',
      phone: friend.phone || '',
      profile_image: friend.profile_image || '',
      app_user_id: friend.app_user_id || '',
      instruments: friend.instruments || []
    });
    setEditingFriend(friend);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      
      // Check for duplicates before creating/updating
      if (!editingFriend || 
          editingFriend.name !== formData.name.trim() || 
          editingFriend.email !== formData.email.trim() ||
          editingFriend.app_user_id !== formData.app_user_id) {
        
        const params = new URLSearchParams();
        if (formData.name.trim()) params.append('name', formData.name.trim());
        if (formData.email.trim()) params.append('email', formData.email.trim());
        if (formData.app_user_id) params.append('app_user_id', formData.app_user_id);
        // Add exclude_id when editing to prevent self-comparison
        if (editingFriend) {
          params.append('exclude_id', editingFriend.id);
        }
        
        if (params.toString()) {
          const duplicateCheckResponse = await fetch(
            `${API}/friends/check-duplicate?${params.toString()}`, 
            {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            }
          );
          
          if (duplicateCheckResponse.ok) {
            const duplicateData = await duplicateCheckResponse.json();
            
            if (duplicateData.is_duplicate) {
              setMessage(`${duplicateData.message}`);
              setLoading(false);
              return;
            }
          }
        }
      }
      
      const url = editingFriend 
        ? `${API}/friends/${editingFriend.id}`
        : `${API}/friends`;
      
      const method = editingFriend ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setMessage(`Friend ${editingFriend ? 'updated' : 'added'} successfully!`);
        onFriendsUpdate();
        
        // For edits, show success message for 2 seconds before closing form
        if (editingFriend) {
          setTimeout(() => {
            resetForm();
          }, 2000);
        } else {
          // For new friends, close form immediately
          resetForm();
        }
      } else {
        const errorData = await response.json();
        setMessage(`${errorData.detail || 'Error saving friend'}`);
      }
    } catch (error) {
      setMessage('Error saving friend');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (friend) => {
    setFriendToDelete(friend);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!friendToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const url = `${API}/friends/${friendToDelete.id}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage('Friend removed successfully!');
        onFriendsUpdate();
        setShowDeleteModal(false);
        setFriendToDelete(null);
      } else {
        setMessage('Error removing friend');
      }
    } catch (error) {
      setMessage('Error removing friend');
      console.error('Error:', error);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setFriendToDelete(null);
  };

  const addAppUser = (user) => {
    setFormData({
      name: user.username,
      email: '',
      phone: '',
      profile_image: '',
      app_user_id: user.id,
      instruments: []
    });
    setFriendType('app_user');
    setSearchResults([]);
    setSearchQuery('');
    setMessage(`Selected app user: ${user.username}`);
    // Check for duplicates when app user is selected
    checkFriendDuplicate('', '', user.id);
  };

  const checkFriendDuplicate = async (name, email, appUserId) => {
    if (!name && !email && !appUserId) {
      setDuplicateCheckMessage('');
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (name && name.length >= 2) params.append('name', name);
      if (email) params.append('email', email);
      if (appUserId) params.append('app_user_id', appUserId);
      
      // Exclude current friend when editing
      if (editingFriend && editingFriend.id) {
        params.append('exclude_id', editingFriend.id);
      }

      if (params.toString()) {
        const response = await fetch(
          `${API}/friends/check-duplicate?${params.toString()}`, 
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.is_duplicate) {
            setDuplicateCheckMessage(`Warning: ${data.message}`);
          } else {
            setDuplicateCheckMessage('Available: Friend details are available');
          }
        }
      }
    } catch (error) {
      console.error('Error checking friend duplicate:', error);
      setDuplicateCheckMessage('');
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchAppUsers(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Debounced duplicate checking for friend data
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.name || formData.email || formData.app_user_id) {
        checkFriendDuplicate(formData.name, formData.email, formData.app_user_id);
      } else {
        setDuplicateCheckMessage('');
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.name, formData.email, formData.app_user_id]);

  return (
    <div className="settings-tab-content">
      <div className="tab-header">
        <div className="tab-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Friend
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowInviteModal(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            Invite via Email
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {showForm && (
        <div className="form-modal-overlay">
          <div className="form-modal">
            <div className="modal-header">
              <h4>{editingFriend ? 'Edit Friend' : 'Add New Friend'}</h4>
              <button onClick={resetForm} className="close-btn">&times;</button>
            </div>
            
            <form onSubmit={handleSubmit} className="settings-form">
              <div className="form-field">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter friend's name"
                  required
                />
                {isCheckingDuplicate && (
                  <small className="field-help checking">
                    🔍 Checking availability...
                  </small>
                )}
                {duplicateCheckMessage && !isCheckingDuplicate && (
                  <small className={`field-help ${duplicateCheckMessage.startsWith('Warning') ? 'warning' : duplicateCheckMessage.startsWith('Available') ? 'success' : 'info'}`}>
                    {duplicateCheckMessage.replace(/^(Warning|Available|Info): /, '')}
                  </small>
                )}
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="Enter email"
                  />
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <TagInput
                tags={formData.instruments}
                onChange={(newTags) => setFormData({...formData, instruments: newTags})}
                placeholder="e.g., Guitar, Bass, Vocals"
                label="Instruments"
              />

              <ImageUpload
                currentImage={formData.profile_image}
                onImageChange={(imageUrl) => setFormData({...formData, profile_image: imageUrl})}
                label="Friend Profile Image"
              />

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn btn-primary">
                  {loading ? 'Saving...' : (editingFriend ? 'Update' : 'Add Friend')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {friends.length === 0 ? (
        <div className="empty-state">
          <p>No friends found. Click "Add Friend" to connect with other musicians.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="gigs-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Instruments</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {friends.map(friend => (
                <tr key={friend.id}>
                  <td>
                    {friend.profile_image ? (
                      <img 
                        src={
                          friend.profile_image.startsWith('/uploads/') 
                            ? `${BASE_URL}/api${friend.profile_image}`
                            : friend.profile_image.startsWith('/api/uploads/')
                            ? `${BASE_URL}${friend.profile_image}`
                            : friend.profile_image.startsWith('http')
                            ? friend.profile_image
                            : `${BASE_URL}/api/uploads/${friend.profile_image}`
                        } 
                        alt={friend.name} 
                        className="table-profile-image"
                        crossOrigin="anonymous"
                        onError={(e) => {
                          console.error('Friend image failed to load:', e.target.src);
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeD0iMTAiIHk9IjEwIj4KPHBhdGggZD0iTTIwIDIxVjE5QzIwIDE3LjkgMTkuMSAxNyAxOCAxN0g2QzQuOSAxNyA0IDE3LjkgNCAxOVYyMSIgc3Ryb2tlPSIjOWNhM2FmIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8Y2lyY2xlIGN4PSIxMiIgY3k9IjkiIHI9IjQiIHN0cm9rZT0iIzljYTNhZiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cjwvc3ZnPgo=';
                        }}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div className="placeholder-avatar">
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                          <circle cx="20" cy="20" r="20" fill="#f3f4f6"/>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" x="10" y="10">
                            <path d="M20 21V19C20 17.9 19.1 17 18 17H6C4.9 17 4 17.9 4 19V21" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="9" r="4" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </svg>
                      </div>
                    )}
                  </td>
                  <td><strong>{friend.name}</strong></td>
                  <td>{friend.email || '—'}</td>
                  <td>{formatPhoneNumber(friend.phone)}</td>
                  <td>
                    {friend.instruments && friend.instruments.length > 0 ? (
                      <div className="instruments-tags">
                        {friend.instruments.map((instrument, index) => (
                          <span key={index} className="instrument-tag-display">
                            {instrument}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="no-instruments">—</span>
                    )}
                  </td>
                  <td>
                    <div className="actions-inline">
                      <button
                        className="btn-icon-action btn-edit"
                        onClick={() => handleEdit(friend)}
                        title="Edit friend"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button
                        className="btn-icon-action btn-delete"
                        onClick={() => handleDeleteClick(friend)}
                        title="Delete friend"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3,6 5,6 21,6"/>
                          <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal">
          <div className="modal-content small-modal">
            <div className="modal-header">
              <h4>Remove Friend?</h4>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to remove <strong>{friendToDelete?.name}</strong> from your friends list?</p>
              <p><small>This action cannot be undone.</small></p>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                onClick={cancelDelete} 
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={confirmDelete} 
                className="btn btn-danger"
              >
                Remove Friend
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Email Invitation Modal */}
      {showInviteModal && (
        <div className="modal" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invite Friend via Email</h3>
              <button 
                className="modal-close"
                onClick={() => setShowInviteModal(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="modal-body">
              <p>Send an invitation email to someone to join your gig management network. They'll receive an email with a registration link.</p>
              
              <div className="form-group">
                <label htmlFor="invite-email">Email Address</label>
                <input
                  id="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="friend@example.com"
                  disabled={isInviting}
                />
              </div>

              {inviteStatus && (
                <div className={`message ${inviteStatus.success ? 'message-success' : 'message-error'}`}>
                  {inviteStatus.message}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-primary"
                onClick={async () => {
                  if (!inviteEmail.trim()) {
                    setInviteStatus({
                      success: false,
                      message: 'Please enter an email address'
                    });
                    return;
                  }

                  setIsInviting(true);
                  setInviteStatus(null);

                  try {
                    const result = await onInviteUser(inviteEmail.trim());
                    setInviteStatus(result);
                    
                    if (result.success) {
                      setTimeout(() => {
                        setShowInviteModal(false);
                        setInviteEmail('');
                        setInviteStatus(null);
                      }, 2000);
                    }
                  } catch (error) {
                    setInviteStatus({
                      success: false,
                      message: 'Failed to send invitation'
                    });
                  } finally {
                    setIsInviting(false);
                  }
                }}
                disabled={isInviting || !inviteEmail.trim()}
              >
                {isInviting ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
                      <path d="M21 12a9 9 0 11-6.219-8.56"/>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    Send Invitation
                  </>
                )}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setInviteStatus(null);
                }}
                disabled={isInviting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Users Tab Component (Admin Only)
export const UsersTab = ({ users, onUsersUpdate, currentUser }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [emailInvitations, setEmailInvitations] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState('users'); // 'users' or 'invitations'
  const [showDeleteInviteModal, setShowDeleteInviteModal] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState(null);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const API = `${BASE_URL}/api`;

  const fetchEmailInvitations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/email-invitations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setEmailInvitations(data);
      } else {
        console.error('Failed to fetch email invitations');
      }
    } catch (error) {
      console.error('Error fetching email invitations:', error);
    }
  };

  const handleDeleteInviteClick = (invitation) => {
    setInviteToDelete(invitation);
    setShowDeleteInviteModal(true);
  };

  const confirmDeleteInvite = async () => {
    if (!inviteToDelete) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/email-invitations/${inviteToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setMessage(`${result.message}`);
        fetchEmailInvitations(); // Refresh the invitations list
        setShowDeleteInviteModal(false);
        setInviteToDelete(null);
      } else {
        const errorData = await response.json();
        setMessage(`${errorData.detail || 'Error deleting invitation'}`);
      }
    } catch (error) {
      setMessage('Error deleting invitation');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteInvite = () => {
    setShowDeleteInviteModal(false);
    setInviteToDelete(null);
  };

  // Fetch email invitations on component mount
  React.useEffect(() => {
    fetchEmailInvitations();
  }, []);

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setMessage(`${result.message}`);
        onUsersUpdate(); // Refresh the users list
        setShowDeleteModal(false);
        setUserToDelete(null);
      } else {
        const errorData = await response.json();
        setMessage(`${errorData.detail || 'Error deleting user'}`);
      }
    } catch (error) {
      setMessage('Error deleting user');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateStr;
    }
  };

  const getRegistrationTypeIcon = (method) => {
    if (method === 'invitation') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" title="Invited User">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
          <polyline points="22,6 12,13 2,6"></polyline>
        </svg>
      );
    }
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" title="Direct Registration">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    );
  };

  return (
    <div className="settings-tab-content">
      <div className="tab-header">
        <h3>User Management</h3>
        <div className="user-stats">
          <span className="stat-item">
            <strong>{users.length}</strong> total users
          </span>
          <span className="stat-item">
            <strong>{emailInvitations.length}</strong> email invitations
          </span>
        </div>
      </div>

      {/* Sub-tabs for Users vs Email Invitations */}
      <div className="sub-tabs">
        <button 
          className={`sub-tab ${activeSubTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('users')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <circle cx="16" cy="7" r="3"></circle>
          </svg>
          Registered Users ({users.length})
        </button>
        <button 
          className={`sub-tab ${activeSubTab === 'invitations' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('invitations')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
          </svg>
          Email Invitations ({emailInvitations.length})
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') || message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {/* Users Tab Content */}
      {activeSubTab === 'users' && (
        <>
          {users.length === 0 ? (
            <div className="empty-state">
              <p>No users found.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="gigs-table users-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Registration</th>
                    <th>Joined</th>
                    <th>Gigs</th>
                    <th>Friends</th>
                    <th>Invited By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-info">
                          <strong>{user.username}</strong>
                          {['admin', 'markperic'].includes(user.username) && (
                            <span className="admin-badge">Admin</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="registration-info">
                          {getRegistrationTypeIcon(user.registration_method)}
                          <span className="registration-method">
                            {user.registration_method === 'invitation' ? 'Invited' : 'Direct'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="date-info">
                          {formatDate(user.created_at)}
                        </div>
                      </td>
                      <td>
                        <span className="count-badge">{user.gig_count || 0}</span>
                      </td>
                      <td>
                        <span className="count-badge">{user.friend_count || 0}</span>
                      </td>
                      <td>
                        {user.invited_by ? (
                          <span className="inviter-info">{user.invited_by}</span>
                        ) : (
                          <span className="no-inviter">—</span>
                        )}
                      </td>
                      <td>
                        {!['admin', 'markperic'].includes(user.username) ? (
                          <button
                            className="btn-icon-action btn-delete"
                            onClick={() => handleDeleteClick(user)}
                            title="Delete user"
                            disabled={loading}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3,6 5,6 21,6"/>
                              <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
                            </svg>
                          </button>
                        ) : (
                          <span className="no-actions">Protected</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Email Invitations Tab Content */}
      {activeSubTab === 'invitations' && (
        <>
          {emailInvitations.length === 0 ? (
            <div className="empty-state">
              <p>No email invitations sent yet.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="gigs-table invitations-table">
                <thead>
                  <tr>
                    <th>Recipient Email</th>
                    <th>Status</th>
                    <th>Sent Date</th>
                    <th>Expires</th>
                    <th>Invited By</th>
                    <th>Registration URL</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {emailInvitations.map(invitation => (
                    <tr key={invitation.id}>
                      <td>
                        <strong>{invitation.recipient_email}</strong>
                      </td>
                      <td>
                        <span className={`status-badge status-${invitation.status}`}>
                          {invitation.status}
                        </span>
                      </td>
                      <td>
                        <div className="date-info">
                          {formatDate(invitation.created_at)}
                        </div>
                      </td>
                      <td>
                        <div className="date-info">
                          {formatDate(invitation.expires_at)}
                        </div>
                      </td>
                      <td>
                        <span className="inviter-info">{invitation.inviter_username}</span>
                      </td>
                      <td>
                        <button
                          className="btn-link btn-small"
                          onClick={() => navigator.clipboard.writeText(invitation.registration_url)}
                          title="Copy registration URL"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5,15H4a2,2,0,0,1-2-2V4A2,2,0,0,1,4,2H13a2,2,0,0,1,2,2V5"></path>
                          </svg>
                          Copy Link
                        </button>
                      </td>
                      <td>
                        <button
                          className="btn-icon-action btn-delete"
                          onClick={() => handleDeleteInviteClick(invitation)}
                          title="Delete invitation"
                          disabled={loading}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3,6 5,6 21,6"/>
                            <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal">
          <div className="modal-content small-modal">
            <div className="modal-header">
              <h4>Delete User?</h4>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete user <strong>{userToDelete?.username}</strong>?</p>
              <div className="delete-warning">
                <p><strong>This will permanently delete:</strong></p>
                <ul>
                  <li>{userToDelete?.gig_count || 0} gigs</li>
                  <li>{userToDelete?.friend_count || 0} friends/contacts</li>
                  <li>All associated data (artists, venues, invitations)</li>
                </ul>
                <p><small><strong>This action cannot be undone.</strong></small></p>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                onClick={cancelDelete} 
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={confirmDelete} 
                className="btn btn-danger"
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Invitation Confirmation Modal */}
      {showDeleteInviteModal && (
        <div className="modal">
          <div className="modal-content small-modal">
            <div className="modal-header">
              <h4>Delete Email Invitation?</h4>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the invitation sent to <strong>{inviteToDelete?.recipient_email}</strong>?</p>
              <div className="delete-warning">
                <p><strong>This will permanently:</strong></p>
                <ul>
                  <li>Remove the invitation from the system</li>
                  <li>Invalidate the registration link</li>
                  <li>Prevent the recipient from using this invitation to register</li>
                </ul>
                <p><small><strong>This action cannot be undone.</strong></small></p>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                onClick={cancelDeleteInvite} 
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={confirmDeleteInvite} 
                className="btn btn-danger"
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};